import React, { useState, useEffect, useRef, startTransition } from "react";
import MenuIcon from "@mui/icons-material/Menu";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import { Language, Package, PackageContent } from "../../Types/types";
import ImageIcon from "@mui/icons-material/Image";
import CircularProgress from "@mui/material/CircularProgress";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import VideocamIcon from "@mui/icons-material/Videocam";
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import PanoramaPhotosphereSelectIcon from "@mui/icons-material/PanoramaPhotosphereSelect";
import { useTheme, useMediaQuery } from "@mui/material";
import { useLanguage } from "../LanguageContext";
import IconWithStyle from "./iconWithStyle";
import StepItem from "./StepItem";
import ContentRenderer from "./contentRenderer";
import Button from "../Button";
import { FiInfo } from "react-icons/fi";
import DescriptionModal from "./descriptionModal";
import translationManualDetail from "./translationManualDetail";
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../Manual_List/fetch/store";
import {
  fetchStepThumbnail,
  fetchPackageById,
  fetchContentById,
} from "../Manual_List/fetch/slices";
import { cleanBase64ImageData } from "../Manual_List/encodeImage";
import logo from "../../assets/ar encyclopedy.png";

const ManualDetail: React.FC = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [activeStep, setActiveStep] = useState<number | null>(null);
  const [pdfWidth, setPdfWidth] = useState<number | null>(null);
  const [contentWidth, setContentWidth] = useState<number | null>(null);
  const [guideLabel, setGuideLabel] = useState<string>("");
  const [stepsData, setStepsData] = useState<PackageContent[]>([]);
  const [manualDescription, setManualDescription] = useState<string>("");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingThumbnailStep, setLoadingThumbnailStep] = useState<
    number | null
  >(null);
  const [contentUrl, setContentUrl] = useState<string>("");
  const [fileSize, setFileSize] = useState<string>("");

  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();

  const thumbnails = useSelector(
    (state: RootState) => state.data.thumbnailUrl.data
  );

  const location = useLocation();
  const { guideId } = useParams<{ guideId: string }>();
  const contentRef = useRef<HTMLDivElement>(null);
  const [filterType, setFilterType] = useState<string[]>([]);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const { language } = useLanguage();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const t = translationManualDetail[language as Language];

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        const response = await dispatch(
          fetchPackageById({ id: guideId!, locale: language })
        );
        const guideData = response.payload as Package;

        if (!guideData) {
          throw new Error(`Guide with ID ${guideId} not found`);
        }

        startTransition(() => {
          setGuideLabel(guideData.label);
          setManualDescription(guideData.description);
          setStepsData(guideData.packageContents || []);

          if (
            guideData.packageContents &&
            guideData.packageContents.length > 0
          ) {
            const firstStep = guideData.packageContents[0];
            setActiveStep(firstStep.id);
          }
        });
      } catch (error) {
        console.error("Failed to load guide details", error);
        navigate(`/`);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [guideId, navigate, language, dispatch]);

  useEffect(() => {
    if (activeStep != null) {
      setLoadingThumbnailStep(activeStep);
      dispatch(fetchStepThumbnail({ id: activeStep.toString() })).then(() => {
        setLoadingThumbnailStep(null);
      });
    }
  }, [activeStep, dispatch]);

  useEffect(() => {
    const updateDimensions = () => {
      const drawerWidth = isDrawerOpen ? 240 : 0;
      const reductionPercentage = 0.75;

      if (contentRef.current) {
        const availableWidth = window.innerWidth - drawerWidth;
        const adjustedWidth = availableWidth * reductionPercentage;
        setContentWidth(adjustedWidth);
        setPdfWidth(adjustedWidth - 20);
      }
    };

    updateDimensions();
    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, [isDrawerOpen]);

  const toggleDrawer = () => setIsDrawerOpen(!isDrawerOpen);
  const goBack = () => {
    const state = sessionStorage.getItem("manualsListState");
    if (state) {
      const { search, itemsPerPage, page, categories, subCategories, order } =
        JSON.parse(state);
      navigate(
        `/?search=${search}&itemsPerPage=${itemsPerPage}&page=${page}&categories=${categories}&subCategories=${subCategories}&order=${order}&lang=${language}&fromDetail=true`
      );
    } else {
      navigate(`/`);
    }
  };

  const handleStepClick = (id: number) => {
    startTransition(() => {
      setActiveStep(id);
      setContentUrl("");
      toggleDrawer();
    });
  };

  const handleIconClick = (type: string) => {
    setFilterType((current) => {
      const index = current.indexOf(type);
      if (index >= 0) {
        return current.filter((t) => t !== type);
      } else {
        return [...current, type];
      }
    });
  };

  const nextPanorama = (nextImagePath: string) => {
    const nextStep = stepsData.find((step) => step.source === nextImagePath);
    if (nextStep) {
      startTransition(() => {
        setActiveStep(nextStep.id);
      });
    }
  };

  const handleNextStep = async () => {
    if (activeStep !== null) {
      const currentIndex = stepsData.findIndex(
        (step) => step.id === activeStep
      );
      if (currentIndex < stepsData.length - 1) {
        const nextStepId = stepsData[currentIndex + 1].id;
        startTransition(() => {
          setActiveStep(nextStepId);
          setContentUrl("");
        });
      }
    }
  };

  const handlePreviousStep = async () => {
    if (activeStep !== null) {
      const currentIndex = stepsData.findIndex(
        (step) => step.id === activeStep
      );
      if (currentIndex > 0) {
        const previousStepId = stepsData[currentIndex - 1].id;
        startTransition(() => {
          setActiveStep(previousStepId);
          setContentUrl("");
        });
      }
    }
  };

  const handleModalOpen = () => {
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const filteredSteps =
    filterType.length > 0
      ? stepsData.filter((step) => filterType.includes(step.type))
      : stepsData;

  const fullscreenClass = isFullscreen
    ? "fixed inset-0 z-50 bg-black"
    : "flex flex-col min-h-screen bg-gray-100";

  const handleStepUpdate = async (newStepId: number) => {
    setActiveStep(newStepId);
    setLoading(true);

    try {
      const contentResponse = await dispatch(
        fetchContentById({ id: newStepId.toString() })
      );
      const { url, size } = contentResponse.payload as {
        url: string;
        size: string;
      };
      setContentUrl(url);
      setFileSize(size);
    } catch (error) {
      console.error("Failed to fetch content for the new step", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`${fullscreenClass} transition-all duration-300 ease-in-out dark:bg-black`}
    >
      {!isFullscreen && (
        <>
          <div className="flex sm:flex-row sm:items-center flex-col ml-4 ">
            <img
              src={logo}
              alt="Logo"
              className="h-8 sm:block hidden w-auto cursor-pointer mb-4"
            />

            <div className="sm:p-4 sm:mb-4 sm:mt-1 mt-5 whitespace-normal break-word sm:max-w-[500px] max-w-[240px]">
              <h1 className="font-semibold sm:text-3xl text-xl text-purple">
                {guideLabel || "Step Detail"}
              </h1>
            </div>
          </div>

          <div className="absolute top-4 right-4 z-20 flex items-center gap-4">
            {!isDrawerOpen && (
              <img
                src="/Icons/infoIcon.svg"
                onClick={handleModalOpen}
                className="cursor-pointer text-gray-800 hover:text-gray-600 transition-colors dark:text-white size-6"
              />
            )}
            <MenuIcon
              onClick={toggleDrawer}
              className="cursor-pointer text-gray-800 hover:text-gray-600 transition-colors dark:text-white"
              fontSize="large"
            />
          </div>

          <div
            className={`fixed inset-y-0 right-0 transform transition-transform duration-300 ease-in-out z-10 ${
              isDrawerOpen ? "translate-x-0" : "translate-x-full"
            } bg-white dark:bg-darkBackground shadow-xl p-4 pb-8 md:w-96 w-full max-h-screen flex flex-col`}
          >
            <div className="flex items-center mt-1">
              <div
                className="flex gap-2 items-center cursor-pointer group"
                onClick={goBack}
              >
                <ArrowBackIosNewIcon className="text-gray-800 hover:text-gray-600 mr-auto bg-lightPurple p-2 rounded-md group-[]:hover:opacity-70 transition-all" />
                <p className="text-xl font-light dark:text-white">Back</p>
              </div>
            </div>

            <div className="sticky top-0 bg-white dark:bg-darkBackground z-20 pb-4">
              <div className="flex justify-between gap-4 sm:gap-12 my-6">
                <IconWithStyle
                  IconComponent={ImageIcon}
                  type="image"
                  isSelected={filterType.includes("image")}
                  handleIconClick={handleIconClick}
                />
                <IconWithStyle
                  IconComponent={PictureAsPdfIcon}
                  type="pdf"
                  isSelected={filterType.includes("pdf")}
                  handleIconClick={handleIconClick}
                />
                <IconWithStyle
                  IconComponent={VideocamIcon}
                  type="video"
                  isSelected={filterType.includes("video")}
                  handleIconClick={handleIconClick}
                />
                <IconWithStyle
                  IconComponent={ViewInArIcon}
                  type="object"
                  isSelected={filterType.includes("object")}
                  handleIconClick={handleIconClick}
                />
                <IconWithStyle
                  IconComponent={PanoramaPhotosphereSelectIcon}
                  type="panorama"
                  isSelected={filterType.includes("panorama")}
                  handleIconClick={handleIconClick}
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto hide-scrollbar">
              <div className="space-y-3">
                {filteredSteps.map((step) => (
                  <StepItem
                    key={step.id}
                    step={step}
                    handleStepClick={handleStepClick}
                    language={language}
                  />
                ))}
              </div>
            </div>
          </div>

          <DescriptionModal
            open={isModalOpen}
            handleClose={handleModalClose}
            description={manualDescription}
            language={language}
          />
        </>
      )}

      <div
        ref={contentRef}
        className={`flex-grow flex flex-col justify-center items-center p-4 text-black transition-margin duration-300 ease-in-out max-w-full`}
        style={
          isSmallScreen || isMediumScreen
            ? { maxHeight: "calc(100vh - 160px)" }
            : {}
        }
      >
        {loading ? (
          <CircularProgress />
        ) : (
          activeStep && (
            <ContentRenderer
              step={{
                ...stepsData.find((s) => s.id === activeStep),
                thumbnail: cleanBase64ImageData(thumbnails[activeStep]),
              }}
              isDrawerOpen={isDrawerOpen}
              handleThumbnailClick={() => handleStepUpdate(activeStep!)}
              showThumbnail={contentUrl === ""}
              pdfWidth={pdfWidth}
              nextPanorama={nextPanorama}
              contentUrl={contentUrl!}
              fileSize={fileSize}
              setIsFullscreen={setIsFullscreen}
              updateContentUrl={setContentUrl}
              handleStepUpdate={handleStepUpdate}
              setLoading={setLoading}
            />
          )
        )}
      </div>

      {!isFullscreen && (
        <div className="flex flex-col sm:mt-4 sm:space-y-2 sm:items-center sm:p-4 bg-gray-100 p-4 fixed bottom-0 left-0 right-0 sm:static sm:bg-transparent">
          <Button
            buttonText={t.previous}
            onClick={handlePreviousStep}
            className={`w-full sm:max-w-xs ${
              activeStep === stepsData[0]?.id
                ? "cursor-not-allowed opacity-50 bg-purple text-white"
                : "bg-darkPurple hover:bg-purple text-white"
            }`}
          />
          <Button
            buttonText={t.next}
            onClick={handleNextStep}
            className={`w-full sm:max-w-xs mt-2 ${
              activeStep === stepsData[stepsData.length - 1]?.id
                ? "cursor-not-allowed opacity-50 bg-purple text-white"
                : "bg-darkPurple hover:bg-purple text-white"
            }`}
          />
        </div>
      )}
    </div>
  );
};

export default ManualDetail;
